import { useState, useEffect, useRef } from "react";
import FormAction from "./FormAction";
import AuthService from "../services/auth.service";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { jwtDecode } from "jwt-decode";

export default function Signup() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const otpInputRef = useRef(null);

  const referral_key = searchParams.get("referral_key") || null;

  // Steps: "phone" | "otp" | "account_type" | "business" | "register"
  const [step, setStep] = useState("phone");
  const [phone_number, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [isBusinessAccount, setIsBusinessAccount] = useState(null); // null | "personal" | "business"
  const [businessForm, setBusinessForm] = useState({
    name: "",
    address_line_1: "",
    address_line_2: "",
    city: "",
    province: "",
    postal_code: "",
    country: "South Africa",
    email: "",
    phone: "",
    website: "",
    monthly_volume_target: "",
    billing_type: "",
  });
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    gender: "",
    password: "",
    confirm_password: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    const currentUser = AuthService.getCurrentUser();
    if (currentUser) {
      if (jwtDecode(currentUser["token"]).exp > Date.now() / 1000) {
        navigate("/parcels");
      }
    }
  }, []);

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    if (!phone_number.trim()) return;
    setError("");
    setIsLoading(true);
    try {
      await AuthService.sendRegistrationOtp(phone_number.trim());
      setStep("otp");
      setResendCooldown(60);
      setTimeout(() => otpInputRef.current?.focus(), 100);
    } catch (err) {
      setError(
        err?.response?.data?.message || "Failed to send OTP. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otp.trim()) return;
    setError("");
    setIsLoading(true);
    try {
      await AuthService.verifyRegistrationOtp(phone_number.trim(), otp.trim());
      setStep("account_type");
    } catch (err) {
      setError(
        err?.response?.data?.message || "Invalid OTP. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setError("");
    setIsLoading(true);
    try {
      await AuthService.sendRegistrationOtp(phone_number.trim());
      setResendCooldown(60);
      setOtp("");
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to resend OTP.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAccountTypeContinue = (e) => {
    e.preventDefault();
    if (!isBusinessAccount) {
      setError("Please select an account type.");
      return;
    }
    setError("");
    if (isBusinessAccount === "business") {
      setStep("business");
    } else {
      setStep("register");
    }
  };

  const handleBusinessContinue = (e) => {
    e.preventDefault();
    if (!businessForm.name.trim()) {
      setError("Business name is required.");
      return;
    }
    setError("");
    setStep("register");
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm_password) {
      setError("Passwords do not match.");
      return;
    }
    setError("");
    setIsLoading(true);

    try {
      const data = await AuthService.register(
        phone_number.trim(),
        form.first_name,
        form.last_name,
        "rider",
        form.gender || null,
        form.email,
        form.password,
        referral_key,
      );

      if (data.token) {
        if (isBusinessAccount === "business") {
          const business = {
            name: businessForm.name.trim(),
            address_line_1: businessForm.address_line_1.trim() || undefined,
            address_line_2: businessForm.address_line_2.trim() || undefined,
            city: businessForm.city.trim() || undefined,
            province: businessForm.province.trim() || undefined,
            postal_code: businessForm.postal_code.trim() || undefined,
            country: businessForm.country.trim() || undefined,
            email: businessForm.email.trim() || undefined,
            phone: businessForm.phone.trim() || undefined,
            website: businessForm.website.trim() || undefined,
            account_tier: "business",
            monthly_volume_target: businessForm.monthly_volume_target
              ? Number(businessForm.monthly_volume_target)
              : undefined,
            billing_type: businessForm.billing_type || undefined,
          };

          try {
            console.log("Creating business with data:", business);
            const businessData = await AuthService.createBusiness(business);
            console.log("Business creation successful:", businessData);
            // Update user in localStorage with new business
            AuthService.refreshUserWithBusiness(businessData);
          } catch (businessErr) {
            console.error("Business creation failed:", businessErr);
            setError(
              businessErr?.response?.data?.message ||
                "Business creation failed. Please try again from the dashboard.",
            );
            // Still redirect to dashboard but show error
            await new Promise((resolve) => setTimeout(resolve, 2000));
          }
        }

        if (isBusinessAccount === "business") {
          navigate("/business/dashboard");
          return;
        }

        if (location?.state?.redirect_to !== undefined) {
          navigate(location.state.redirect_to, { state: location.state });
        } else {
          window.location.replace("/parcels");
        }
      } else {
        setError(
          data?.response?.data?.message ||
            "Registration failed. Please try again.",
        );
      }
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          "Registration failed. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const redirectToLogin = () => {
    if (location?.state?.redirect_to !== undefined) {
      navigate("/login", { state: location.state });
    } else {
      window.location.replace("/login");
    }
  };

  const inputClass =
    "block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent";

  return (
    <div className="flex flex-col gap-4 w-full">
      {/* Step 1 — Phone */}
      {step === "phone" && (
        <>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Create account</h2>
            <p className="mt-1 text-sm text-gray-500">
              Enter your cell number to get started.
            </p>
          </div>

          {error && (
            <div
              className="p-4 text-sm text-red-800 rounded-lg bg-red-50"
              role="alert"
            >
              {error}
            </div>
          )}

          <form onSubmit={handleRequestOtp} className="flex flex-col gap-4">
            <div>
              <label
                htmlFor="phone_number"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Cell number
              </label>
              <input
                id="phone_number"
                type="tel"
                required
                value={phone_number}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="e.g. 0821234567"
                className={inputClass}
              />
            </div>
            <FormAction
              handleSubmit={handleRequestOtp}
              loading={isLoading}
              text="Send OTP"
            />
          </form>

          <div className="text-center flex gap-2 justify-center">
            <span className="text-black text-base font-medium">
              Already have an account?
            </span>
            <span
              className="text-purple-600 text-base font-medium cursor-pointer"
              onClick={redirectToLogin}
            >
              Sign In
            </span>
          </div>
        </>
      )}

      {/* Step 2 — OTP */}
      {step === "otp" && (
        <>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Verify number</h2>
            <p className="mt-1 text-sm text-gray-500">
              We sent a one-time PIN to{" "}
              <span className="font-medium text-gray-700">{phone_number}</span>.
            </p>
          </div>

          {error && (
            <div
              className="p-4 text-sm text-red-800 rounded-lg bg-red-50"
              role="alert"
            >
              {error}
            </div>
          )}

          <form onSubmit={handleVerifyOtp} className="flex flex-col gap-4">
            <div>
              <label
                htmlFor="otp"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                One-time PIN
              </label>
              <input
                ref={otpInputRef}
                id="otp"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                required
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                placeholder="123456"
                className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 tracking-widest text-center text-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
              />
            </div>
            <FormAction
              handleSubmit={handleVerifyOtp}
              loading={isLoading}
              text="Verify number"
            />
          </form>

          <div className="text-center flex gap-2 justify-center text-sm">
            <span className="text-gray-500">Didn't receive it?</span>
            <button
              type="button"
              onClick={handleResend}
              disabled={resendCooldown > 0 || isLoading}
              className={`font-medium ${resendCooldown > 0 ? "text-gray-400 cursor-default" : "text-purple-600 cursor-pointer hover:text-purple-700"}`}
            >
              {resendCooldown > 0
                ? `Resend in ${resendCooldown}s`
                : "Resend OTP"}
            </button>
          </div>

          <button
            type="button"
            onClick={() => {
              setStep("phone");
              setError("");
              setOtp("");
            }}
            className="text-center text-sm text-gray-400 hover:text-gray-600"
          >
            ← Change number
          </button>
        </>
      )}

      {/* Step 3 — Account type */}
      {step === "account_type" && (
        <>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              Who are you registering for?
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Tell us how you'll be using Parcelin.
            </p>
          </div>

          {error && (
            <div
              className="p-4 text-sm text-red-800 rounded-lg bg-red-50"
              role="alert"
            >
              {error}
            </div>
          )}

          <form
            onSubmit={handleAccountTypeContinue}
            className="flex flex-col gap-4"
          >
            <div className="flex flex-col gap-3">
              <label
                className={`flex items-start gap-3 rounded-lg border-2 px-4 py-3 cursor-pointer transition-colors ${
                  isBusinessAccount === "personal"
                    ? "border-violet-500 bg-violet-50"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <input
                  type="radio"
                  name="account_type"
                  value="personal"
                  checked={isBusinessAccount === "personal"}
                  onChange={() => setIsBusinessAccount("personal")}
                  className="mt-0.5 accent-violet-600"
                />
                <div>
                  <p className="text-sm font-semibold text-gray-900">
                    Personal account
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    I'm sending parcels for myself.
                  </p>
                </div>
              </label>

              <label
                className={`flex items-start gap-3 rounded-lg border-2 px-4 py-3 cursor-pointer transition-colors ${
                  isBusinessAccount === "business"
                    ? "border-violet-500 bg-violet-50"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <input
                  type="radio"
                  name="account_type"
                  value="business"
                  checked={isBusinessAccount === "business"}
                  onChange={() => setIsBusinessAccount("business")}
                  className="mt-0.5 accent-violet-600"
                />
                <div>
                  <p className="text-sm font-semibold text-gray-900">
                    Business account
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    I'm registering on behalf of a business.
                  </p>
                </div>
              </label>
            </div>

            <FormAction
              handleSubmit={handleAccountTypeContinue}
              loading={isLoading}
              text="Continue"
            />
          </form>
        </>
      )}

      {/* Step 4 — Business details (only if business account) */}
      {step === "business" && (
        <>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              Business details
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Tell us about your business.
            </p>
          </div>

          {error && (
            <div
              className="p-4 text-sm text-red-800 rounded-lg bg-red-50"
              role="alert"
            >
              {error}
            </div>
          )}

          <form
            onSubmit={handleBusinessContinue}
            className="flex flex-col gap-3"
          >
            {/* Business name */}
            <div>
              <label
                htmlFor="biz_name"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Business name <span className="text-red-500">*</span>
              </label>
              <input
                id="biz_name"
                type="text"
                required
                value={businessForm.name}
                onChange={(e) =>
                  setBusinessForm({ ...businessForm, name: e.target.value })
                }
                placeholder="Tech Solutions SA"
                className={inputClass}
              />
            </div>

            {/* Address */}
            <div>
              <label
                htmlFor="address_line_1"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Address line 1{" "}
                <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <input
                id="address_line_1"
                type="text"
                value={businessForm.address_line_1}
                onChange={(e) =>
                  setBusinessForm({
                    ...businessForm,
                    address_line_1: e.target.value,
                  })
                }
                placeholder="123 Innovation Street"
                className={inputClass}
              />
            </div>

            <div>
              <label
                htmlFor="address_line_2"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Address line 2{" "}
                <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <input
                id="address_line_2"
                type="text"
                value={businessForm.address_line_2}
                onChange={(e) =>
                  setBusinessForm({
                    ...businessForm,
                    address_line_2: e.target.value,
                  })
                }
                placeholder="Suite 45"
                className={inputClass}
              />
            </div>

            <div className="flex gap-3">
              <div className="flex-1">
                <label
                  htmlFor="biz_city"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  City
                </label>
                <input
                  id="biz_city"
                  type="text"
                  value={businessForm.city}
                  onChange={(e) =>
                    setBusinessForm({ ...businessForm, city: e.target.value })
                  }
                  placeholder="Johannesburg"
                  className={inputClass}
                />
              </div>
              <div className="flex-1">
                <label
                  htmlFor="biz_province"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Province
                </label>
                <select
                  id="biz_province"
                  value={businessForm.province}
                  onChange={(e) =>
                    setBusinessForm({
                      ...businessForm,
                      province: e.target.value,
                    })
                  }
                  className={inputClass}
                >
                  <option value="">Select</option>
                  <option value="Gauteng">Gauteng</option>
                  <option value="Western Cape">Western Cape</option>
                  <option value="KwaZulu-Natal">KwaZulu-Natal</option>
                  <option value="Eastern Cape">Eastern Cape</option>
                  <option value="Limpopo">Limpopo</option>
                  <option value="Mpumalanga">Mpumalanga</option>
                  <option value="North West">North West</option>
                  <option value="Free State">Free State</option>
                  <option value="Northern Cape">Northern Cape</option>
                </select>
              </div>
            </div>

            <div className="flex gap-3">
              <div className="flex-1">
                <label
                  htmlFor="postal_code"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Postal code
                </label>
                <input
                  id="postal_code"
                  type="text"
                  value={businessForm.postal_code}
                  onChange={(e) =>
                    setBusinessForm({
                      ...businessForm,
                      postal_code: e.target.value,
                    })
                  }
                  placeholder="2000"
                  className={inputClass}
                />
              </div>
              <div className="flex-1">
                <label
                  htmlFor="biz_country"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Country
                </label>
                <input
                  id="biz_country"
                  type="text"
                  value={businessForm.country}
                  onChange={(e) =>
                    setBusinessForm({
                      ...businessForm,
                      country: e.target.value,
                    })
                  }
                  placeholder="South Africa"
                  className={inputClass}
                />
              </div>
            </div>

            {/* Contact */}
            <div>
              <label
                htmlFor="biz_email"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Business email{" "}
                <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <input
                id="biz_email"
                type="email"
                value={businessForm.email}
                onChange={(e) =>
                  setBusinessForm({ ...businessForm, email: e.target.value })
                }
                placeholder="info@techsolutions.co.za"
                className={inputClass}
              />
            </div>

            <div>
              <label
                htmlFor="biz_phone"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Business phone{" "}
                <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <input
                id="biz_phone"
                type="tel"
                value={businessForm.phone}
                onChange={(e) =>
                  setBusinessForm({ ...businessForm, phone: e.target.value })
                }
                placeholder="+27 11 123 4567"
                className={inputClass}
              />
            </div>

            <div>
              <label
                htmlFor="biz_website"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Website{" "}
                <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <input
                id="biz_website"
                type="url"
                value={businessForm.website}
                onChange={(e) =>
                  setBusinessForm({ ...businessForm, website: e.target.value })
                }
                placeholder="https://www.example.co.za"
                className={inputClass}
              />
            </div>

            {/* Shipping preferences */}
            <div>
              <label
                htmlFor="monthly_volume"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Est. monthly parcel volume{" "}
                <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <input
                id="monthly_volume"
                type="number"
                min="0"
                value={businessForm.monthly_volume_target}
                onChange={(e) =>
                  setBusinessForm({
                    ...businessForm,
                    monthly_volume_target: e.target.value,
                  })
                }
                placeholder="500"
                className={inputClass}
              />
            </div>

            <div>
              <label
                htmlFor="billing_type"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Billing preference{" "}
                <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <select
                id="billing_type"
                value={businessForm.billing_type}
                onChange={(e) =>
                  setBusinessForm({
                    ...businessForm,
                    billing_type: e.target.value,
                  })
                }
                className={inputClass}
              >
                <option value="">Select billing type</option>
                <option value="monthly_invoicing">Monthly invoicing</option>
                <option value="order_to_order">Order to order</option>
                <option value="prepaid">Prepaid Wallet</option>
              </select>
            </div>

            <FormAction
              handleSubmit={handleBusinessContinue}
              loading={isLoading}
              text="Continue"
            />
          </form>

          <button
            type="button"
            onClick={() => {
              setStep("account_type");
              setError("");
            }}
            className="text-center text-sm text-gray-400 hover:text-gray-600"
          >
            ← Back
          </button>
        </>
      )}

      {/* Step 5 — Personal details / Registration form */}
      {step === "register" && (
        <>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Your details</h2>
            <p className="mt-1 text-sm text-gray-500">
              Almost there — fill in your info to finish.
            </p>
          </div>

          {error && (
            <div
              className="p-4 text-sm text-red-800 rounded-lg bg-red-50"
              role="alert"
            >
              {error}
            </div>
          )}

          <form onSubmit={handleRegister} className="flex flex-col gap-3">
            {/* Cell — read-only, verified */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cell number
              </label>
              <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500">
                <span className="flex-1">{phone_number}</span>
                <span className="text-xs text-green-600 font-medium">
                  ✓ Verified
                </span>
              </div>
            </div>

            <div className="flex gap-3">
              <div className="flex-1">
                <label
                  htmlFor="first_name"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  First name
                </label>
                <input
                  id="first_name"
                  type="text"
                  required
                  value={form.first_name}
                  onChange={(e) =>
                    setForm({ ...form, first_name: e.target.value })
                  }
                  placeholder="Jane"
                  className={inputClass}
                />
              </div>
              <div className="flex-1">
                <label
                  htmlFor="last_name"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Last name
                </label>
                <input
                  id="last_name"
                  type="text"
                  required
                  value={form.last_name}
                  onChange={(e) =>
                    setForm({ ...form, last_name: e.target.value })
                  }
                  placeholder="Doe"
                  className={inputClass}
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Email
              </label>
              <input
                id="email"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="jane@example.com"
                className={inputClass}
              />
            </div>

            <div>
              <label
                htmlFor="gender"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Gender{" "}
                <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <select
                id="gender"
                value={form.gender}
                onChange={(e) => setForm({ ...form, gender: e.target.value })}
                className={inputClass}
              >
                <option value="">Prefer not to say</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Password
              </label>
              <input
                id="password"
                type="password"
                required
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="••••••••"
                className={inputClass}
              />
            </div>

            <div>
              <label
                htmlFor="confirm_password"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Confirm password
              </label>
              <input
                id="confirm_password"
                type="password"
                required
                value={form.confirm_password}
                onChange={(e) =>
                  setForm({ ...form, confirm_password: e.target.value })
                }
                placeholder="••••••••"
                className={inputClass}
              />
            </div>

            <FormAction
              handleSubmit={handleRegister}
              loading={isLoading}
              text="Create account"
            />
          </form>

          <button
            type="button"
            onClick={() => {
              setStep(
                isBusinessAccount === "business" ? "business" : "account_type",
              );
              setError("");
            }}
            className="text-center text-sm text-gray-400 hover:text-gray-600"
          >
            ← Back
          </button>
        </>
      )}
    </div>
  );
}
